"use client";

import { Shield, HardHat, CircleCheck, TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function SafetyESGCommitment() {
  const {
    ref: sectionRef,
    isVisible: sectionVisible
  } = useRevealOnScroll<HTMLElement>({
    once: true,
    threshold: 0.15
  });
  const {
    ref: cardsRef,
    isVisible: cardsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.2
  });
  const safetyMetrics = [{
    icon: Shield,
    label: "Zero Harm Target",
    value: "0",
    unit: "Lost Time Injuries (2024)"
  }, {
    icon: HardHat,
    label: "Safety Training",
    value: "10,000+",
    unit: "Hours Annually"
  }, {
    icon: CircleCheck,
    label: "ISO Certified",
    value: "45001",
    unit: "OH&S Management"
  }, {
    icon: TrendingUp,
    label: "Safety Score",
    value: "98%",
    unit: "Compliance Rate"
  }];
  const esgInitiatives = [{
    title: "Environmental Stewardship",
    description: "Minimizing water usage, protecting biodiversity, and implementing progressive rehabilitation across all sites."
  }, {
    title: "Community Partnerships",
    description: "Supporting local employment, education programs, and infrastructure development in host communities."
  }, {
    title: "Governance & Transparency",
    description: "Adhering to strict US corporate governance standards and maintaining full disclosure to stakeholders."
  }, {
    title: "Climate Action",
    description: "Targeting net-zero operational emissions by 2040 through renewable energy integration and efficiency improvements."
  }];
  return <section ref={sectionRef} className={`relative bg-dark-background py-20 transition-opacity duration-[var(--duration-slow)] ease-[var(--ease-smooth)] ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      <div className="container mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {}
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-3">
                Safety First
              </h2>
              <p className="text-foreground-dark/80 text-base leading-relaxed">
                At united mining power, safety is non‑negotiable. Our operations are
                guided by rigorous protocols and continuous training to ensure
                every team member returns home safely.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {safetyMetrics.map((metric, i) => {
              const IconComponent = metric.icon;
              return <div key={i} data-index={i} className="group relative bg-primary/20 backdrop-blur-sm rounded-lg p-6 border border-border/20 transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] hover:bg-primary/30 hover:border-border/40 hover:shadow-medium">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-full bg-accent/20 transition-transform duration-[var(--duration-medium)] ease-[var(--ease-spring)] group-hover:scale-110">
                        <IconComponent className="w-6 h-6 text-accent-dark" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-2xl font-display font-bold text-foreground">
                          {metric.value}
                        </div>
                        <div className="text-sm font-semibold text-foreground/90 mt-1">
                          {metric.label}
                        </div>
                        <div className="text-xs text-foreground/60 mt-0.5">
                          {metric.unit}
                        </div>
                      </div>
                    </div>
                  </div>;
            })}
            </div>
          </div>

          {}
          <div ref={cardsRef} className="space-y-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-3">
                ESG Commitment
              </h2>
              <p className="text-foreground-dark/80 text-base leading-relaxed">
                Our environmental, social, and governance practices are central
                to building long‑term value and trust with investors,
                communities, and partners.
              </p>
            </div>

            <div className="space-y-4">
              {esgInitiatives.map((initiative, i) => <Card key={i} data-index={i} className={`relative overflow-hidden bg-background/95 backdrop-blur-sm border border-border/30 shadow-soft transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)] ${cardsVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`} style={{
              transitionDelay: cardsVisible ? `${i * 100}ms` : "0ms"
            }}>
                  <div className={`absolute left-0 top-0 bottom-0 w-1 bg-accent transition-transform duration-[var(--duration-slow)] ease-[var(--ease-out-back)] origin-left ${cardsVisible ? "scale-x-100" : "scale-x-0"}`} style={{
                transitionDelay: cardsVisible ? `${i * 100 + 200}ms` : "0ms"
              }} />
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg text-foreground">
                      {initiative.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {initiative.description}
                    </p>
                  </CardContent>
                </Card>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}